import CryptoJS from 'crypto-js';
import axios from 'axios';
import encryptAES256GCM from '@utils/encryptAES';
import generateVersionHash from '@utils/generateVersionHash';
import {
  attempt,
  createCancelToken,
  createStoreFetcher,
  isApiError,
} from '@/lib/http';
import { btoa, atob, showAlert } from '../../lib/helpers';
import api from './constants';

const register = {
  namespaced: true,

  state() {
    return {
      step: 1,
      data: {
        merchant_criteria: '',
        noRekening: '',
        validRekening: false,
        accountType: '1',
        qrType: '',
        qrTypeInterface: [],
        needInterface: false,
        merchantModel: null,
        clientVersion : '',
        accessTokenEndpoint :'',
        channelCode: '',
        user: {
          email: '',
          mobilePhone: '',
          password: '',
        },
      },
      channelQrList: [],
      uploadProgress: 0,
      onUpload: false,
      uploadName: '',
      loading: false,
      errors: [],
      referenceNumber: '',
      registeredId: '',
      qrisError: false,
      OnValidateQRIS: false,
      requestData: null,
    };
  },

  mutations: {
    setStep(state, step) {
      state.step = step;
    },
    setEmailUser(state, email) {
      state.data.user.email = email;
    },
    setNoHandphoneUser(state, mobilePhone) {
      state.data.user.mobilePhone = mobilePhone;
    },
    merchantCriteriaData(state, merchant_criteria) {
      // console.log(merchant_criteria)
      state.data.merchant_criteria = merchant_criteria;
    },
    setNoRekening(state, noRekening) {
      state.data.noRekening = noRekening;
    },
    setValidRekening(state, validRekening) {
      state.data.validRekening = validRekening;
    },
    setAccountType(state, accountType) {
      state.data.accountType = accountType;
    },
    setAccessEndpoint(state, accessTokenEndpoint) {
      state.data.accessTokenEndpoint = accessTokenEndpoint;
    },
    setQrType(state, qrType) {
      state.data.qrType = qrType;
    },
    setQrTypeInterface(state, qrTypeInterface) {
      state.data.qrTypeInterface = qrTypeInterface;
    },
    setNeedInterface(state, needInterface) {
      state.data.needInterface = needInterface;
    },
    setMerchantModel(state, merchantModel) {
      state.data.merchantModel = merchantModel;
    },
    setClientVersion(state, clientVersion) {
      state.data.clientVersion = clientVersion;
    },
    setUserData(state, user) {
      state.data.user = Object.assign(state.data.user, user);
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setOnUpload(state, onUpload) {
      state.onUpload = onUpload;
    },
    setUploadName(state, uploadName) {
      state.uploadName = uploadName;
    },
    setOnValidateQRIS(state, status) {
      state.OnValidateQRIS = status;
    },
    setUploadProgress(state, uploadProgress) {
      state.uploadProgress = uploadProgress;
    },
    setErrors(state, errors) {
      state.errors = errors;
    },
    setQrisError(state, status) {
      state.qrisError = status;
    },
    setReferenceNumber(state, payload) {
      state.referenceNumber = payload.reference_number;
      state.registeredId = payload.id;
    },
    clearErrors(state) {
      state.errors = [];
    },
    clearErrorVariable(state, variable) {
      state.errors = { ...state.errors, [variable]: [] };
    },
    clearData(state) {
      state.step = 1;
      state.data.noRekening = '';
      state.data.validRekening = false;
      state.data.accountType = '1';
      state.data.qrType = '';
      state.data.qrTypeInterface = [];
      state.data.needInterface = false;
      state.data.merchantModel = null;
      state.data.merchant_criteria = '';
      state.data.channelCode = '';
      state.data.user.email = '';
      state.data.user.mobilePhone = '';
      state.data.user.password = '';
      state.uploadProgress = 0;
      state.onUpload = false;
      state.uploadName = '';
      state.loading = false;
      state.errors = [];
      state.referenceNumber = '';
    },
    setCancelation(state, payload) {
      state.requestData = payload;
    },
    setSelectedInterfaces(state, selectedInterfaces) {
      state.data.qrTypeInterface = selectedInterfaces;
    },
    setChannelCode(state, channelCode) {
      state.data.channelCode = channelCode;
    },
    setChannelQrList(state, channelQrList) {
      state.channelQrList = channelQrList;
    },
  },

  getters: {
    errors: (state) => state.errors,
  },

  actions: {
    /**
     * Loads the QR channels offered on the registration and revision forms.
     *
     * The list only feeds a dropdown, so it neither clears nor fills the field
     * errors of the form it sits in. A form that is already showing validation
     * errors keeps them when the list finishes loading.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @returns {Promise<Array|false>} The channel list, or false when it could
     *   not be loaded.
     */
    async getQRChannelList(ctx, { date }) {
      const http = createStoreFetcher(ctx, { errorsMutation: null });

      return attempt(async () => {
        const res = await http.post(api.CHANNEL_QR, {
          version: generateVersionHash('', date),
        });

        ctx.commit('setChannelQrList', res.data);
        return res.data;
      }, false);
    },
    /**
     * Validates a bank account number while the user is still typing.
     *
     * Each keystroke cancels the request in flight, so the answer always belongs
     * to the number currently on screen. A canceled request raises no alert and
     * leaves the loading flag to the request that replaced it.
     *
     * Failures stay silent because the field already shows the reason inline;
     * an alert on every keystroke would be noise. Existing errors are kept so
     * that checking the account does not clear the rest of the form.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {string} params.no_rekening - Account number to validate.
     * @returns {Promise<boolean>} True when the account number is valid.
     */
    async validate(ctx, { date, no_rekening }) {
      if (ctx.state.requestData) ctx.state.requestData.cancel();

      const source = createCancelToken();
      ctx.commit('setCancelation', {
        cancel: source.cancel,
        msg: 'Loading...',
      });

      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        await http.post(
          api.VALIDATE_REKENING,
          {
            account_number: no_rekening,
            version: generateVersionHash(no_rekening, date),
          },
          { cancelToken: source.token, resetErrors: false, silent: true }
        );

        return true;
      }, false);
    },
    /**
     * Submits the merchant registration.
     *
     * A rejected submission raises an alert on its own, and a validation failure
     * additionally fills the per-field errors that the form renders under each
     * input. The caller only needs to tell the two apart, which it does through
     * the returned value.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {Object} params.all_data - Form values collected by the wizard.
     * @param {boolean} params.isIncludeAPI - Whether the API interface applies.
     * @returns {Promise<Object|false>} The created registration, or false when
     *   the submission was rejected.
     */
    async saveRegistration(ctx, { date, all_data, isIncludeAPI }) {
      const payload = {
        channel_qr: ctx.state.data.channelCode,
        merchant_model: all_data.merchant_model,
        nmid: all_data.nmid,
        email: ctx.state.data.user.email,
        mobile_phone: ctx.state.data.user.mobilePhone,
        mcc: all_data.mcc,
        mdr_on_us: all_data.mdr_on_us,
        mdr_off_us: all_data.mdr_off_us,
        province_name: all_data.province_name,
        city_name: all_data.city_name,
        sub_district_name: all_data.sub_district_name,
        urban_name: all_data.urban_name,
        merchant_name: all_data.merchant_name,
        company_name: all_data.company_name,
        owner_name: all_data.owner_name,
        ktp_no: all_data.ktp_no,
        npwp_no: all_data.npwp_no,
        merchant_criteria: all_data.merchant_criteria,
        postal_code: all_data.postal_code,
        address: all_data.address,
        bcko: all_data.bcko,
        cmer: all_data.cmer,
        referral_code: all_data.referral_code,
        account_type: ctx.state.data.accountType,
        qr_type: ctx.state.data.qrType,
        merchant_type: '1',
        account_number: ctx.state.data.noRekening,
        documents: all_data.documents,
        phone: all_data.phone,
        phone_area_code: all_data.phone_area_code,
        approver_1: all_data.approver_1,
        approver_2: all_data.approver_2,
        mid: all_data.mid,
        tid: all_data.tid,
        endpoint: all_data.endpoint,
        merchant_mobile_phone: all_data.merchant_mobile_phone,
        id_merchant: all_data.id_merchant,
        id_aggregator: all_data.id_aggregator,
        type_registrasi: all_data.type_registrasi,
        ip_address: all_data.ip_address,
        parent_account_type: all_data.parent_account_type,
        notif_sticker_status: all_data.notif_sticker_status,
        version: generateVersionHash(
          all_data.merchant_name +
            all_data.owner_name +
            all_data.merchant_criteria +
            ctx.state.data.qrType +
            all_data.postal_code,
          date
        ),
      };

      if (ctx.state.data.qrType == 2) {
        payload.client_version = ctx.state.data.clientVersion;
      }
      
      if (isIncludeAPI) {
        payload.is_need_interface = ctx.state.data.qrTypeInterface.length > 0;
      }

      if (ctx.state.data.qrType == 2 && ctx.state.data.clientVersion == 2) {
        payload.client_partner_id = all_data.client_partner_id;
        payload.client_secret = all_data.client_partner_secret;
        payload.public_key_inbound = encryptAES256GCM(
          all_data.public_key_inbound || ''
        );
        payload.access_token_endpoint = all_data.access_token_endpoint;
      }

      let data;

      if (all_data.mdr_file) {
        data = new FormData();
        Object.keys(payload).forEach((key) => {
          if (payload[key] !== null && payload[key] !== undefined) {
            if (typeof payload[key] === 'object' && !Array.isArray(payload[key])) {
              data.append(key, JSON.stringify(payload[key]));
            } else if (Array.isArray(payload[key])) {
              payload[key].forEach((item) => {
                data.append(`${key}[]`, item);
              });
            } else {
              data.append(key, payload[key]);
            }
          }
        });

        if (isIncludeAPI && ctx.state.data.qrTypeInterface) {
          ctx.state.data.qrTypeInterface.forEach((item) => {
            data.append('qr_type_interface[]', item);
          });
        }

        data.append('mdr_file', all_data.mdr_file);
      } else {
        if (isIncludeAPI) {
          payload.qr_type_interface = ctx.state.data.qrTypeInterface;
        }
        data = payload;
      }

      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        const res = await http.post(api.REGISTRASI_INDEX, data);

        showAlert(
          ctx,
          res.message || 'Update Success !',
          'mdi-check-circle',
          'green'
        );

        return res.data;
      }, false);
    },
    async uploadDocument(ctx, { date, file, nama, doc_type }) {
      console.log(date);
      ctx.commit('setQrisError', false);
      ctx.commit('setOnUpload', true);
      ctx.commit('setUploadName', `Upload File ' ${nama} '`);
      if (doc_type == 4) ctx.commit('setOnValidateQRIS', true);
      const data = new FormData();
      data.append('file', file);
      data.append('doc_type', doc_type);
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/document/document/upload`,
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'multipart/form-data',
          'X-Origin-Path': window.location.pathname
        },
        onUploadProgress(progressEvent) {
          ctx.commit(
            'setUploadProgress',
            parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100),
              10
            )
          );
        },
        data,
      };
      return axios(config)
        .then((response) => {
          if (doc_type == 4 && !response.data.data.data.success) {
            ctx.commit('setQrisError', true);
          }
          return response.data.data;
        })
        .catch((error) => {
          if (error.response.status == '403') {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: error.response.data.message,
                icon: 'mdi-alert',
                color: 'red',
              },
              { root: true }
            );
          } else if (error.response.status == '401') {
            ctx.dispatch('logout', null, { root: true });
          }
          return false;
        })
        .finally(() => {
          ctx.commit('setLoading', false);
          ctx.commit('setOnUpload', false);
          if (doc_type == 4) ctx.commit('setOnValidateQRIS', false);
        });
    },

    /**
     * Checks whether the email and mobile phone are still available.
     *
     * A rejected check fills the per-field errors so that step 4 can mark the
     * offending input rather than only showing an alert.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {string} params.email - Email to check.
     * @param {string} params.mobile_phone - Mobile phone to check.
     * @returns {Promise<boolean>} True when both are available.
     */
    async checkEmailPhone(ctx, { date, email, mobile_phone }) {
      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        await http.post(api.CHECK_EMAIL_PHONE, {
          email,
          mobile_phone,
          version: generateVersionHash(email + mobile_phone, date),
        });

        return true;
      }, false);
    },

    /**
     * Requests the one-time password for the registering user.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @returns {Promise<Object|false>} The OTP details, or false when rejected.
     */
    async getOTP(ctx, { date }) {
      const { email, mobilePhone } = ctx.state.data.user;
      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        const res = await http.post(api.GET_OTP, {
          email,
          mobile_phone: mobilePhone,
          version: generateVersionHash(email + mobilePhone, date),
        });

        return res.data;
      }, false);
    },

    /**
     * Redeems the one-time password entered by the user.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {string} params.otp - Code entered by the user.
     * @returns {Promise<Object|false>} The result, or false when rejected.
     *
     * The failure reason is stored as a plain message rather than a field map,
     * because step 5 renders it in its own snackbar next to the OTP input. The
     * shared alert is suppressed so the user is told once, not twice.
     */
    async useOTP(ctx, { date, otp }) {
      const { mobilePhone } = ctx.state.data.user;
      const http = createStoreFetcher(ctx, { errorsMutation: null });

      try {
        const res = await http.post(
          api.USE_OTP,
          {
            otp_code: otp,
            mobile_phone: mobilePhone,
            version: generateVersionHash(otp + mobilePhone, date),
          },
          { silent: true }
        );

        return res.data;
      } catch (error) {
        if (!isApiError(error)) throw error;

        ctx.commit('setErrors', error.message);
        return false;
      }
    },

    /**
     * Asks for a fresh one-time password.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @returns {Promise<Object|false>} The OTP details, or false when rejected.
     */
    async resendOTP(ctx, { date }) {
      const { email, mobilePhone } = ctx.state.data.user;
      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        const res = await http.post(api.RESEND_OTP, {
          email,
          mobile_phone: mobilePhone,
          version: generateVersionHash(email + mobilePhone, date),
        });

        return res.data;
      }, false);
    },

    async getDataAutofill(ctx, { date, id_aggregator, id_merchant }) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        id_aggregator,
        id_merchant,
        version: btoa(
          CryptoJS.SHA256(
            id_aggregator +
              id_merchant +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/onboarding/registrasi/account-detail`,
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };
      return axios(config)
        .then((response) => {
          if (response.data.code != '200') {
            return false;
          }
          return response.data.data;
        })
        .catch((error) => {
          if (error.response.status == '403') {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: error.response.data.message,
                icon: 'mdi-alert',
                color: 'red',
              },
              { root: true }
            );
          } else if (error.response.status == '401') {
            ctx.dispatch('logout', null, { root: true });
          }
          return false;
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },

    async getMdrList(ctx, { date, mcc, merchant_criteria }) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        mcc,
        merchant_criteria,
        version: btoa(
          CryptoJS.SHA256(
            mcc +
              merchant_criteria +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/onboarding/get-value-mdr`,
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };
      return axios(config)
        .then((response) => {
          if (response.data.code != '200') {
            return false;
          }
          return response.data.data;
        })
        .catch((error) => {
          if (error.response.status == '403') {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: error.response.data.message,
                icon: 'mdi-alert',
                color: 'red',
              },
              { root: true }
            );
          } else if (error.response.status == '401') {
            ctx.dispatch('logout', null, { root: true });
          }
          return false;
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },
  },
};
export default register;
